import type { EnvironmentIndicator, NonProductionEnvironment } from '../lib/environment';

// Option C from the Figma "Env indicator (proposal)" page: a 4px stripe across
// the top of the sidebar plus an overline caption by the logo. Both render
// nothing when `indicator` is null (production and local dev).

const STRIPE_BG: Record<NonProductionEnvironment, string> = {
  staging: 'bg-(--color-env-staging)',
  demo: 'bg-(--color-env-demo)',
};

const CAPTION_TEXT: Record<NonProductionEnvironment, string> = {
  staging: 'text-(--color-env-staging)',
  demo: 'text-(--color-env-demo)',
};

interface Props {
  indicator: EnvironmentIndicator | null;
}

/**
 * Place inside a `relative`/`fixed` container; spans its full width. Add `overflow-hidden`
 * to a rounded container so the stripe follows its corners.
 */
export const EnvironmentStripe = ({
  indicator,
  testId = 'environment-stripe',
}: Props & { testId?: string }) => {
  if (!indicator) return null;
  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${STRIPE_BG[indicator.environment]}`}
    />
  );
};

export const EnvironmentCaption = ({
  indicator,
  collapsed = false,
}: Props & { collapsed?: boolean }) => {
  if (!indicator) return null;
  const color = CAPTION_TEXT[indicator.environment];

  if (collapsed) {
    // "STG"/"DEMO" is cryptic, so screen readers get the full label and mouse users a tooltip.
    return (
      <span
        title={indicator.label}
        className={`text-[10px]/4 font-semibold uppercase tracking-wide ${color}`}
      >
        <span aria-hidden="true">{indicator.shortLabel}</span>
        <span className="sr-only">{indicator.label}</span>
      </span>
    );
  }

  return (
    <span className={`block text-[11px]/4 font-semibold uppercase tracking-wider ${color}`}>
      {indicator.label}
    </span>
  );
};
